import { cn } from "@/lib/utils";
import { cssInterop } from "nativewind";
import type { ReactElement } from "react";
import type { StyleProp, TextStyle } from "react-native";

export type VectorIconBaseProps = {
  size?: number;
  color?: string;
  style?: StyleProp<TextStyle>;
};

type IconProps = VectorIconBaseProps &
  Record<string, unknown> & {
    /** An `@expo/vector-icons` icon family component (e.g. `Ionicons`, `MaterialIcons`). */
    as: unknown;
    /** Icon name — specific to the chosen icon family. */
    name: string;
    className?: string;
  };

function IconImpl({ as: IconComponent, className: _className, ...props }: IconProps) {
  const ResolvedIcon = IconComponent as (iconProps: Record<string, unknown>) => ReactElement;
  return <ResolvedIcon {...props} />;
}

cssInterop(IconImpl, {
  className: {
    target: "style",
    nativeStyleToProp: {
      height: "size",
      width: "size",
    },
  },
});

/**
 * A wrapper component for `@expo/vector-icons` families with Nativewind `className` support via `cssInterop`.
 *
 * @component
 * @example
 * ```tsx
 * import { Ionicons } from '@expo/vector-icons';
 * import { Icon } from '@/components/ui/Icon';
 *
 * <Icon as={Ionicons} name="home" className="text-primary" size={24} />
 * ```
 *
 * @param as - The `@expo/vector-icons` icon family component to render.
 * @param name - Icon name within the chosen family.
 * @param className - Nativewind utility classes applied to the icon.
 * @param size - Icon size (defaults to 24).
 * @param color - Icon color.
 */
function Icon({ as: IconComponent, className, size = 24, ...props }: IconProps) {
  return (
    <IconImpl
      as={IconComponent}
      className={cn("text-foreground", className)}
      size={size}
      {...props}
    />
  );
}

export { Icon };
export type { IconProps };
